import React, {useEffect, useMemo, useRef, useState} from "react";
import {FaClockRotateLeft, FaComment, FaPen, FaTrash} from "react-icons/fa6";
import Button from "../../../../components/button";
import InputText from "../../../../components/input-text";

const CardActivityPanel = ({card, onCreateComment, onUpdateComment, onDeleteComment}) => {
    const [comment, setComment] = useState("");
    const [activePanel, setActivePanel] = useState("comments");
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentText, setEditingCommentText] = useState("");
    const [pendingAction, setPendingAction] = useState(null);
    const commentInputRef = useRef(null);
    const comments = useMemo(() => Array.isArray(card.comments) ? card.comments : [], [card.comments]);
    const history = card.history ?? [];

    useEffect(() => {
        if (!editingCommentId || comments.some(item => item.id === editingCommentId)) return;
        setEditingCommentId(null);
        setEditingCommentText("");
    }, [comments, editingCommentId]);

    const handleCommentChange = ({target}) => {
        setComment(target.value);
        target.style.height = "0px";
        target.style.height = `${Math.min(target.scrollHeight, 160)}px`;
    };

    const addComment = async () => {
        if (!comment.trim()) return;
        setPendingAction("create");
        try {
            await onCreateComment(comment.trim());
            setComment("");
            if (commentInputRef.current) commentInputRef.current.style.height = "64px";
        } catch {
            return;
        } finally {
            setPendingAction(null);
        }
    };

    const startEditing = item => {
        setEditingCommentId(item.id);
        setEditingCommentText(item.text);
    };

    const cancelEditing = () => {
        setEditingCommentId(null);
        setEditingCommentText("");
    };

    const saveComment = async commentId => {
        const text = editingCommentText.trim();
        if (!text) return;
        setPendingAction(`update-${commentId}`);
        try {
            await onUpdateComment(commentId, text);
            cancelEditing();
        } catch {
            return;
        } finally {
            setPendingAction(null);
        }
    };

    const deleteComment = async commentId => {
        setPendingAction(`delete-${commentId}`);
        try {
            await onDeleteComment(commentId);
            if (editingCommentId === commentId) cancelEditing();
        } catch {
            return;
        } finally {
            setPendingAction(null);
        }
    };

    return (
        <aside className="flex h-[640px] min-h-0 flex-col overflow-hidden border-t border-slate-200 bg-white lg:h-auto lg:border-l lg:border-t-0">
            <div className="flex shrink-0 border-b border-slate-200 px-5" role="tablist" aria-label="Atividades do cartão">
                <button type="button" role="tab" aria-selected={activePanel === "comments"} aria-controls="card-comments-panel" onClick={() => setActivePanel("comments")} className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-semibold transition-colors ${activePanel === "comments" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                    <FaComment /> Comentários <span className="font-normal">({comments.length})</span>
                </button>
                <button type="button" role="tab" aria-selected={activePanel === "history"} aria-controls="card-history-panel" onClick={() => setActivePanel("history")} className={`ml-5 flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-semibold transition-colors ${activePanel === "history" ? "border-amber-500 text-slate-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
                    <FaClockRotateLeft /> Histórico <span className="font-normal">({history.length})</span>
                </button>
            </div>

            {activePanel === "comments" && (
                <section id="card-comments-panel" role="tabpanel" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5">
                    <div className="border-b border-slate-100 py-4">
                        <InputText id="card-comment" label="Escreva um comentário..." rows={2} height="h-16 min-h-16 max-h-40 resize-none overflow-y-auto !rounded-lg !border-slate-200" value={comment} onChange={handleCommentChange} textarearef={commentInputRef} />
                        <div className="mt-2 flex justify-end"><Button type="button" target="primary" onClick={addComment} disabled={!comment.trim() || pendingAction !== null} col="!h-8 !px-3">Enviar</Button></div>
                    </div>

                    <div className="divide-y divide-slate-100">
                        {comments.slice().reverse().map(item => (
                            <article key={item.id} className="group flex gap-3 py-4">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">{item.author?.charAt(0)?.toUpperCase() || "U"}</span>
                                <div className="min-w-0 flex-1">
                                    <div className="mb-1 flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <strong className="block truncate text-xs font-semibold text-slate-700">{item.author}</strong>
                                            <span className="block text-[10px] text-slate-300">{item.date}{item.edited ? " • editado" : ""}</span>
                                        </div>
                                        {item.canAlter && editingCommentId !== item.id && (
                                            <span className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                                                <button type="button" onClick={() => startEditing(item)} disabled={pendingAction !== null} className="rounded p-1 text-slate-300 hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 disabled:cursor-not-allowed disabled:opacity-50" title="Editar comentário" aria-label="Editar comentário"><FaPen /></button>
                                                <button type="button" onClick={() => deleteComment(item.id)} disabled={pendingAction !== null} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-600 focus:opacity-100 disabled:cursor-not-allowed disabled:opacity-50" title="Excluir comentário" aria-label="Excluir comentário"><FaTrash /></button>
                                            </span>
                                        )}
                                    </div>
                                    {item.canAlter && editingCommentId === item.id ? (
                                        <div className="mt-2">
                                            <InputText id={`edit-comment-${item.id}`} label="Editar comentário" rows={3} height="h-20" value={editingCommentText} onChange={({target}) => setEditingCommentText(target.value)} />
                                            <div className="mt-2 flex justify-end gap-2">
                                                <Button type="button" onClick={cancelEditing} disabled={pendingAction !== null} col="!h-8 !px-3">Cancelar</Button>
                                                <Button type="button" target="primary" onClick={() => saveComment(item.id)} disabled={!editingCommentText.trim() || pendingAction !== null} col="!h-8 !px-3">Alterar</Button>
                                            </div>
                                        </div>
                                    ) : <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600">{item.text}</p>}
                                </div>
                            </article>
                        ))}
                        {comments.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Nenhum comentário registrado.</p>}
                    </div>
                </section>
            )}

            {activePanel === "history" && (
                <section id="card-history-panel" role="tabpanel" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                    {history.slice().reverse().map((item, index) => (
                        <div key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
                            {index < history.length - 1 && <span className="absolute bottom-0 left-4 top-8 w-px bg-slate-200" aria-hidden="true" />}
                            <span className="relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-50 text-xs text-amber-500"><FaClockRotateLeft /></span>
                            <div className="min-w-0 flex-1 pt-0.5">
                                <p className="text-xs leading-relaxed text-slate-600">{item.author && <strong className="font-semibold text-slate-700">{item.author} </strong>}{item.text}</p>
                                <span className="mt-1 block text-[10px] text-slate-300">{item.date}</span>
                            </div>
                        </div>
                    ))}
                    {history.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Nenhuma atividade registrada.</p>}
                </section>
            )}
        </aside>
    );
};

export default CardActivityPanel;
